import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { VideoCarousel } from "./VideoCarousel";
import { CantoCarousel } from "./CantoCarousel";
import { Gallery } from "./Gallery";
import { PhotoStory } from "./PhotoStory";
import { MalaCounter } from "./MalaCounter";
import { AudioSample } from "./AudioSample";
import { ConversionCTA } from "./ConversionCTA";
import { SponsorBhagavatam } from "./SponsorBhagavatam";
import { GlimpseEntry } from "@/components/glimpse/GlimpseExperience";
import { moduleHasContent, PATH_TAGS } from "@/config/journeys";

const RENDERERS = {
  video_carousel: VideoCarousel,
  canto_carousel: CantoCarousel,
  gallery: Gallery,
  photo_story: PhotoStory,
  mala_counter: MalaCounter,
  audio_sample: AudioSample,
};

// Progressive-disclosure journey block: rendered only AFTER a scratch card is
// revealed. Fades in, nudges the user to scroll, then plays out its visible
// modules (the depth differs per path — "Grand Immersion" for Bhagavatam,
// lighter modules for the others).
export const JourneyBlock = ({ pathKey, journey, giriWhatsapp, bookUrl, slug, sponsor, lang, autoScroll }) => {
  const ref = useRef(null);
  const modules = (journey?.modules || []).filter(
    (m) => m.visible !== false && moduleHasContent(m)
  );

  useEffect(() => {
    if (autoScroll && ref.current) {
      const t = setTimeout(
        () => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        160
      );
      return () => clearTimeout(t);
    }
  }, [autoScroll]);

  // The "Sponsor a Bhagavatam" block lives at the end of the Bhagavatam path,
  // right after the "Bring Srimad Bhagavatam Home" conversion card.
  const showSponsor = pathKey === "bhagavatam" && sponsor?.enabled !== false;
  const isBhagavatam = pathKey === "bhagavatam";

  if (!modules.length && !showSponsor && !isBhagavatam) return null;

  return (
    <motion.section
      ref={ref}
      data-testid={`journey-block-${pathKey}`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="relative scroll-mt-32 border-t border-amber-300/10 pt-10 pb-28 sm:pb-20 md:pt-12"
    >
      <div className="space-y-20 md:space-y-28">
        {isBhagavatam && <GlimpseEntry slug={slug} />}
        {modules.map((m) => {
          if (m.type === "conversion") {
            return (
              <ConversionCTA
                key={m.id}
                module={m}
                giriWhatsapp={giriWhatsapp}
                bookUrl={bookUrl}
                slug={slug}
                pathLabel={PATH_TAGS[pathKey] || pathKey}
              />
            );
          }
          const Renderer = RENDERERS[m.type];
          return Renderer ? <Renderer key={m.id} module={m} /> : null;
        })}
        {showSponsor && (
          <SponsorBhagavatam sponsor={sponsor} slug={slug} lang={lang} />
        )}
      </div>
    </motion.section>
  );
};
